/**
 * The Finance board's read model (GET /finance/board), as
 * backend/src/services/finance/financeBoard.js returns it: one row per
 * handoff to Finance (a UMED obligation, or a private-pay sale), tabs that are eleven eMerge handoff states, and a
 * temporary reconciliation lens of legacy Monday rows outside Main table
 * (authority ruling, Isaiah Cotto, 2026-10-05). No SSN is ever in it;
 * approval facts are present only when `approval_detail` is true.
 */
import api from '../../services/api';
import type { ApiResult, AwaitingCorrection, HandoffCorrected } from './financeApi';
import type { BoardPage } from '../../components/board';
import type { MarkerShape } from '../../components/tide';
import { boardQueryString } from '../scheduling/boardApi';

export type FinanceStateKey =
  | 'not-received' | 'received' | 'accepted' | 'invoiced' | 'terms-recorded' | 'partly-paid'
  | 'written-off' | 'collecting' | 'paid-in-full' | 'closed' | 'returned';

/** A Monday money value: legacy reference, read-only. `value: null` is "not recorded", never $0. */
export interface MondayMoney { value: number | null; source: 'MONDAY'; read_only: true }

/** The Monday Finance row on a lens row. Legacy reference, read-only; it decides nothing. */
export interface MondayLegacy {
  account_id: string;
  /** How many Monday Finance rows name this resident. */
  items: number;
  status: { word: string; color: string | null; known: boolean } | null;
  facility: { id: string; name: string | null } | null;
  sale_value: MondayMoney;
  payments_received: MondayMoney;
  remaining: MondayMoney;
  overdue: MondayMoney;
  terms: { text: string; source: 'MONDAY'; read_only: true } | null;
  refund: { amount: number | null; on: string | null; source: 'MONDAY' } | null;
  reversal_date: { on: string; source: 'MONDAY' } | null;
  dcf_correction_date: { on: string; source: 'MONDAY' } | null;
  deceased_date: { on: string; source: 'MONDAY' } | null;
  invoice_created: { value: boolean; source: 'MONDAY' } | null;
  /** This row's "as of" (Monday was retired Oct 15, 2026). Absent from an older server. */
  as_of?: import("./ledgerApi").MondayAsOf | null;
  updated_at: string | null;
  source: 'MONDAY';
  label: string;
  legacy: true;
  reference: true;
  read_only: true;
}

export interface FinanceHandoffFacts {
  /** null until Finance receives the handoff in eMerge. */
  handoff_id: string | null;
  /** 20261008b: a UMED obligation (received or not), or a private-pay sale a PA handed straight to Finance (no UMED). */
  kind: 'UMED' | 'PRIVATE_PAY';
  /** "Private pay (no UMED)" on a private-pay row; null otherwise. */
  label: string | null;
  /** null on a private-pay row: no UMED obligation. */
  umed_action_id: string | null;
  umed_case_id: string | null;
  received: boolean;
  state: string | null;
  version: number | null;
  handed_at: string | null;
  opened_from: { event: string; words: string } | null;
  reason: string | null;
  source_rule: string | null;
  attention: 'UMED_OBLIGATION_CANCELLED' | null;
  payment_state: string | null;
  accepted: { at: string; by: { user_id: string; name: string | null } | null } | null;
  invoice: { reference: string; created_on: string | null } | null;
  terms: { term_months: number; monthly_cents: number | null; total_cents: number | null; rule_version: string | null } | null;
  closed: { at: string; reason: string | null } | null;
  /** A private-pay row: the PA who handed it (the actor of its HANDOFF_RECEIVED event). */
  handed_by: { name: string | null } | null;
  /** A private-pay row: what was sold, as the handoff carries it -- never Finance's own terms. */
  sold: { total_cents: number; term_months: number; monthly_cents: number; remainder_cents: number; rule_version: string | null; pricing_version: string | null; pricing_version_id: string | null; payer?: 'FAMILY' | 'FACILITY' | null } | null;
  /** Ruling #1 (2026-10-07): amendments recorded on this handoff -- count = rows, last_on = latest day (Florida). */
  corrected: HandoffCorrected | null;
  /** PAR 01 (2026-10-08): APPROVED - SFS CORRECTION -- the months still awaiting their correction. Null (or absent before the deploy): none. */
  awaiting_correction?: AwaitingCorrection | null;
  source: 'EMERGE';
}

/** Processes 5 and 6: the resident's UMED flags on a board row. count = rows; information only, never an amount. */
export interface FinanceBoardFlags {
  count: number; last_on: string; kinds: string[];
  latest: { kind: string; label: string; summary: string; on: string };
}

export interface FinanceBoardRow {
  id: string;
  /**
   * HANDOFF = a Main-table row; HELD = a UMED case approved in eMerge with no Finance handoff (its own population,
   * outside Main table; most are held by a Monday Finance link); MONDAY_ONLY = a legacy Monday row not reconciled yet.
   */
  kind: 'HANDOFF' | 'HELD' | 'MONDAY_ONLY';
  /** null: the Monday row names no resident (it cannot open the dossier). */
  resident: { id: string; name: string } | null;
  facility: { id: string; name: string | null; source: 'EMERGE' } | null;
  /** 2026-10-14: no current building -> the resident's CounselEAR Preferred Clinic (not a confirmed building). */
  counselear_clinic?: { id: string; name: string | null } | null;
  emerge_state: { key: FinanceStateKey | string; word: string; color: string | null; marker: MarkerShape; source: 'EMERGE' } | null;
  handoff: FinanceHandoffFacts | null;
  deceased_reported: { effective_date: string | null; channel: string; source: 'EMERGE' } | null;
  liability: { cents: number | null; status: string | null; verification_source: string | null; effective_from: string | null; source: 'EMERGE' } | null;
  sale: { amount: number | null; amount_source: string | null; source: 'EMERGE' } | null;
  notes_count: number | null;
  /** Processes 5 and 6: UMED flags for this resident (null = none). Absent from a payload before the deploy. */
  flags?: FinanceBoardFlags | null;
  /** Lens rows only. */
  monday: MondayLegacy | null;
  /** Lens rows only (null otherwise): the resident has an approval in eMerge with no Finance handoff (a HELD row). */
  approved_without_handoff?: boolean | null;
  /** A HELD row: the approved case, the act's date, and where its Monday link points (said, never a condition). */
  held?: {
    umed_case_id: string;
    approved_on: string | null;
    stage: string | null;
    monday_link: { account_id: string | null; points_to: 'THIS_RESIDENT' | 'OTHER_RESIDENT' | 'NO_RESIDENT' | 'MISSING' | 'NONE'; source: 'MONDAY' };
    source: 'EMERGE';
  } | null;
  /**
   * Present only when the caller may read the approval facts; null on a
   * private-pay row (no UMED). `proven` is the approval ACT (approved in
   * eMerge, 2026-10-06), never FLMMIS reflected on its own; `stage` and
   * `finance_state` are the case's own, so a stage Monday set to approved is
   * named as Monday's claim (final verification 2026-10-06, P2-C).
   */
  approval?: { noca: string | null; flmmis: string | null; proven: boolean; approved_on: string | null; stage?: string | null; finance_state?: string | null; source: 'EMERGE' } | null;
  /** Native ATD (approved to dispense): eMerge's own word; status null = blank (not recorded). Absent before the deploy. */
  atd?: { status: string | null; atd_date: string | null; source: 'EMERGE' } | null;
  /** GET /finance/kanban only: where the server placed the card -- its column, its state, its rank inside that state. */
  kanban?: { column: string; state: FinanceStateKey; rank: number };
}
export interface FinanceFilterOption { id: string; name: string; count: number }
export interface FinanceBoardPage extends Omit<BoardPage<FinanceBoardRow>, 'mode' | 'groups'> {
  mode: 'EMERGE';
  default_tab: string;
  lens_tab: string;
  monday_label: string;
  /** Oct 2026: the Monday mirror's "as of" (null when no row). Absent from an older server. */
  monday_as_of?: import("./ledgerApi").MondayAsOf | null;
  /** The eleven states' sum and Main table, from the same counts: equal by construction, stated on screen. */
  buckets: { keys: FinanceStateKey[]; sum: number; main: number };
  filters: { acceptor: string | null; building: string | null; q: string | null; atd?: string | null };
  filter_options: { acceptors: FinanceFilterOption[]; buildings: FinanceFilterOption[]; atd?: { id: string; name: string; count?: number }[] };
  approval_detail: boolean;
  /** The held population (its tab, its count, how many carry a Monday link), under the same scope, search and filters. */
  held?: { tab: string; count: number; linked: number };
}
export interface FinanceBoardQuery {
  tab?: string; q?: string; acceptor?: string; building?: string; atd?: string; sort?: string | null; dir?: 'asc' | 'desc'; page?: number;
  /** Rows per page (the server caps it at 100; omitted = the server's default). The Kanban pages each state with it. */
  limit?: number;
}

/** GET /finance/kanban (2026-10-14): one read for the Kanban -- every column, its states' exact counts, a page of each state. */
export interface FinanceKanbanColumn {
  key: string; label: string; what: string; page: number; limit: number; count: number; has_more: boolean;
  states: Array<{ key: FinanceStateKey; label: string; color: string | null; count: number; has_more: boolean }>;
  cards: FinanceBoardRow[];
}
export interface FinanceKanbanPage {
  board: 'finance-kanban';
  columns: FinanceKanbanColumn[];
  /** Whole-board read only (absent on a column page). */
  total?: number;
  elsewhere?: Array<{ key: string; label: string; tab: string; what: string; count: number }>;
  filter_options?: FinanceBoardPage['filter_options'];
  column?: string;
  approval_detail: boolean;
}
export interface FinanceKanbanQuery { q?: string; acceptor?: string; building?: string; atd?: string; column?: string; page?: number; limit?: number }

export const FINANCE_DEFAULT_TAB = 'open';
export const FINANCE_LENS_TAB = 'monday-only';
export const FINANCE_HELD_TAB = 'held';
/** The server's page cap (financeBoard.js PAGE_MAX). */
export const FINANCE_PAGE_MAX = 100;

export const financeKanbanApi = {
  read: async (p: FinanceKanbanQuery, signal?: AbortSignal): Promise<ApiResult<FinanceKanbanPage>> => {
    const url = `/finance/kanban${boardQueryString({ q: p.q, acceptor: p.acceptor, building: p.building, atd: p.atd, column: p.column, page: p.page, limit: p.limit })}`;
    return (await (signal ? api.get(url, undefined, { signal }) : api.get(url))) as ApiResult<FinanceKanbanPage>;
  },
};

export const financeBoardApi = {
  board: async (p: FinanceBoardQuery): Promise<ApiResult<FinanceBoardPage>> =>
    (await api.get(`/finance/board${boardQueryString({ tab: p.tab, q: p.q, acceptor: p.acceptor, building: p.building, atd: p.atd, sort: p.sort, dir: p.sort ? p.dir : null, page: p.page, limit: p.limit })}`)) as ApiResult<FinanceBoardPage>,
};
